import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Folder } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SubFolderSummary } from '../../../services/api/folderService';
import { folderItemCount } from '../folderCounts';
import {
    Category,
    getCategoryColorClasses,
} from '../../ui-components/category/categoryColors';

interface SubFolderTreeProps {
    root: { _id: string; title: string; category?: Category };
    subFolders: SubFolderSummary[];
    currentId: string;
}

const itemClasses = (isCurrent: boolean) =>
    cn(
        'relative flex items-start gap-2 rounded-md px-2 py-1.5 text-[13px] leading-5',
        isCurrent
            ? 'font-semibold text-foreground'
            : 'font-medium text-subtle-foreground hover:bg-muted hover:text-foreground'
    );

const CurrentMarker = ({ color }: { color: string }) => (
    <span
        aria-hidden="true"
        className={cn(
            'absolute bottom-1.5 left-0 top-1.5 w-0.5 rounded-full',
            color
        )}
    />
);

export const SubFolderTree = ({
    root,
    subFolders,
    currentId,
}: SubFolderTreeProps) => {
    const { t } = useTranslation();
    const rootIsCurrent = root._id === currentId;
    const markerColor = root.category
        ? getCategoryColorClasses(root.category.color).swatch
        : 'bg-foreground';

    return (
        <nav aria-label={t('folders.detail.subFolders')}>
            <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-subtle-foreground">
                {t('folders.overview.title')}
            </h2>
            <Link
                to={`/group/${root._id}`}
                aria-current={rootIsCurrent ? 'page' : undefined}
                className={itemClasses(rootIsCurrent)}
            >
                {rootIsCurrent && <CurrentMarker color={markerColor} />}
                <Folder
                    aria-hidden="true"
                    className="mt-[3px] h-3.5 w-3.5 shrink-0"
                />
                <span className="min-w-0 break-words">{root.title}</span>
            </Link>
            <ul className="mt-0.5 space-y-0.5 pl-0">
                {subFolders.map((sub, index) => {
                    const isCurrent = sub._id === currentId;
                    const isLast = index === subFolders.length - 1;
                    const total = folderItemCount(sub);
                    return (
                        <li key={sub._id} className="relative pl-6">
                            <span
                                aria-hidden="true"
                                className={cn(
                                    'absolute -top-0.5 left-[14px] w-px bg-subtle',
                                    isLast ? 'h-[18px]' : 'bottom-0'
                                )}
                            />
                            <span
                                aria-hidden="true"
                                className="absolute left-[14px] top-4 h-px w-2 bg-subtle"
                            />
                            <Link
                                to={`/group/${sub._id}`}
                                aria-current={isCurrent ? 'page' : undefined}
                                className={itemClasses(isCurrent)}
                            >
                                {isCurrent && (
                                    <CurrentMarker color={markerColor} />
                                )}
                                <span className="min-w-0 flex-1 break-words">
                                    {sub.title}
                                </span>
                                {total > 0 && (
                                    <span className="shrink-0 text-[11px] tabular-nums">
                                        {total}
                                    </span>
                                )}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};
